import './BetbookTopEvent.less';

import React from 'react';
import { SvgIcon } from 'nike-ui/src/icons/SvgIcon';
import moment from 'moment';
import { Selection } from '@/nikeone/components/Tips';
import i18n from '@i18n';
import { eventUrlDescription } from 'nike-ui/src/common/sportEvents';
import MatchIndex from 'nike-ui/src/components/market-statistics/view/MatchIndex';
import { isToday, isTomorrow } from '@/common/client';
import { useReframe } from 'nike-re-framejs';
import { MatchTimer } from 'nike-ui/src/components/turboLive/components/MatchTimer';
import {
    isTop10LiveLoaded$,
    top10LiveBetSelection$$,
    top10LiveMatch$$,
    top10LiveMatchStats$$,
    top10LiveScoreboard$$,
    top10LiveTimer$$,
    top10PrematchMatch$$
} from 'nike-ui/src/components/homepage/subs';
import {
    isShortTimeFormat,
    MatchLiveSign,
    MatchOpponents,
    MatchScoreboard
} from 'nike-ui/src/components/turboLive/components/Match';
import clsx from 'clsx';
import { useTop10LivePush } from 'nike-ui/src/components/homepage/hooks';
import { BetSelection } from 'nike-ui/src/components/turboLive/components/BetSelection';
import { MatchIcon } from 'nike-ui/src/components/turboLive/components/MatchIcons';
import { isRadarStream, isVideoStream } from 'nike-ui/src/components/turboLive/push/overview/handlers';
import { betbookTopEventsMatch$$ } from 'nike-ui/src/components/betbook/subs';
import { MatchDetailLink } from 'nike-ui/src/components/homepage/components/MatchDetailLink';
import { IcoPlaceholder } from 'nike-ui/src/components/homepage/components/HomepageTop5';
import { getScoreboardType } from 'nike-ui/src/components/turboLive/matchExtras';
import { SportIconNui } from 'nike-ui/src/icons/SportIconNui.tsx';
import { getSportNameById } from 'nike-ui/src/common/utils/utils';

const TopEventBox = ({ index, matchInfo, dataAtid, isLiveEvent }) => {
    const { derefSub } = useReframe();
    const matchId = matchInfo?.matchId;
    const betId = matchInfo?.betId;
    useTop10LivePush(matchId, betId, isLiveEvent);
    const dataLoaded = derefSub(isTop10LiveLoaded$);
    const timer = isLiveEvent && derefSub(top10LiveTimer$$(matchId));
    const matchScore = isLiveEvent && derefSub(top10LiveScoreboard$$(matchId));
    const matchStats = derefSub(top10LiveMatchStats$$(matchId));
    const liveSelection = isLiveEvent && derefSub(top10LiveBetSelection$$(betId, matchInfo?.code));
    const match = isLiveEvent
        ? derefSub(top10LiveMatch$$(matchId))
        : derefSub(top10PrematchMatch$$(matchId)) || derefSub(betbookTopEventsMatch$$(matchId));
    if (!match || !matchInfo) {
        return null;
    }
    const hasVideoStream = isVideoStream(match);
    const hasRadarStream = isRadarStream(match);
    const hasAnalysis = match.get('articleId');
    const isMatchLive = match.get('isLive');
    const streamIconTitle = isMatchLive ? 'nui.turbo.live.icon.stream' : 'nui.turbo.live.icon.stream.disabled';
    const formatDate = someDate => {
        const time = moment(someDate).format('HH:mm');

        if (isToday(moment(someDate))) {
            return i18n('enum.calendar.dnes') + ' - ' + time;
        } else if (isTomorrow(moment(someDate))) {
            return i18n('commons.datum.zajtra') + ' - ' + time;
        } else return moment(someDate).format('D.M.') + ' - ' + time;
    };

    const formatTicketsCount = ticketsCount => {
        if (ticketsCount > 4) return ticketsCount.toLocaleString('sk') + ' ' + i18n('betbook.topEvents.numOfTickets');
        else if (1 < ticketsCount && ticketsCount < 5) return ticketsCount + ' ' + i18n('betbook.topEvents.tickets');
        else if (ticketsCount === 1) return ticketsCount + ' ' + i18n('betbook.topEvents.ticket');
    };

    const urlMeta = eventUrlDescription({
        date: matchInfo.betExpiration,
        home: matchInfo.participants[0],
        away: matchInfo.participants[1]
    });
    return (
        <div className="top-event-wrapper bg-white flex flex-col pb-15 mx-20 br-radius-4" data-atid={dataAtid}>
            <div className="flex flex-row br-b-default-light mx-15 py-10">
                <div className="flex-center">
                    <MatchIndex index={index + 1} otherClass="c-burgundy" />
                    <SportIconNui sportId={matchInfo.sportId} className="c-black-25 mr-10 ml-10" isFilled={false} />
                </div>
                <div className="flex flex-1 justify-end">
                    <span className="bg-light-burgundy br-radius-2 flex flex-center c-white text-bold fs-12 px-15 py-5">
                        {formatTicketsCount(matchInfo.ticketsCount)}
                    </span>
                </div>
            </div>
            <div className="flex flex-col my-10 c-black-100 fs-14">
                <div className="flex flex-1 justify-between pr-10">
                    <MatchDetailLink
                        matchId={matchId}
                        home={matchInfo.participants[0]}
                        away={matchInfo.participants[1]}
                        stats={matchStats}
                        meta={urlMeta}
                        className="flex-1"
                    >
                        <MatchOpponents
                            home={matchInfo.participants[0]}
                            away={matchInfo.participants[1]}
                            stats={matchStats}
                        />
                    </MatchDetailLink>
                    {isLiveEvent && (
                        <MatchScoreboard
                            key={index}
                            score={matchScore}
                            sportId={matchInfo.sportId}
                            server={matchScore && matchScore?.get('server')}
                            hasScorePlaceholder
                        />
                    )}
                </div>

                <div className="flex mt-7 fs-12 c-default-light mx-15">
                    {isLiveEvent ? (
                        <div className="flex-center">
                            <MatchLiveSign startTime={timer} otherClass="mr-10" />
                            <MatchTimer
                                scoreboardTypeId={getScoreboardType(match)}
                                matchTimer={timer}
                                otherClass="fs-11 c-default-light justify-between mr-10 font-weight-400"
                                shortTimeFormat={isShortTimeFormat(getSportNameById(matchInfo.sportId))}
                            />
                        </div>
                    ) : (
                        <span className="mr-10 font-weight-400">{formatDate(matchInfo.betExpiration)}</span>
                    )}

                    <span className="text-extra-bold ellipsis flex-1">{matchInfo.betHeader}</span>
                    <div className="flex">
                        {hasAnalysis ? (
                            <SvgIcon imgId="live-analyse" sizeX="16" sizeY="16" className="c-black-20 mx-5" />
                        ) : (
                            <IcoPlaceholder />
                        )}
                        {hasVideoStream ? (
                            <MatchIcon
                                imgId="live-stream"
                                title={streamIconTitle}
                                data-atid="stream-icon"
                                disabled={!isMatchLive}
                            />
                        ) : (
                            <IcoPlaceholder />
                        )}
                        {hasRadarStream ? (
                            <MatchIcon imgId="live-radar" title="nui.turbo.live.icon.radar" data-atid="radar-icon" />
                        ) : (
                            <IcoPlaceholder />
                        )}
                    </div>
                </div>
            </div>
            <div className="flex justify-center">
                <div className="tip-table collapse betbookShadow mx-15">
                    <div className="tip-row tip">
                        {isLiveEvent ? (
                            <div className="bet-selection-height flex br-default-light bets-table-shadow">
                                {liveSelection ? (
                                    <BetSelection
                                        selection={liveSelection}
                                        betId={matchInfo.betId}
                                        isDetail
                                        otherClass={clsx('bg-white fs-12')}
                                        channelEnabled
                                        stopped={false}
                                    />
                                ) : (
                                    <div className={clsx('flex-1 flex-center c-black-50 fs-14')}>
                                        {dataLoaded ? i18n('nui.turbo.live.noBettingEvents') : ''}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <Selection
                                selectionsInRow={1}
                                betId={matchInfo.betId}
                                key={index}
                                selection={{
                                    name: matchInfo.betSelectionName,
                                    odds: matchInfo.odds,
                                    tip: matchInfo.code
                                }}
                                betNumber={matchInfo.betId}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TopEventBox;
